import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';

export type VoiceControlLang = 'en' | 'es' | 'fr';

/**
 * Determines voice-control language from the browser (navigator.language),
 * not from Kiwi app language. The device language drives how the voice engine
 * interprets commands (e.g. "en-US" → English, "es-MX" → Spanish, "fr-CA" → French).
 */
@Injectable({
  providedIn: 'root',
})
export class VoiceControlLanguageService {
  private voiceLang: VoiceControlLang = 'en';

  constructor(private readonly translate: TranslateService) {
    // Determine and cache the voice control language
    this.voiceLang = this.getVoiceControlLanguage();

    // Eagerly load the voice control language translations
    // This ensures translations are available when aria-labels are set
    if (this.voiceLang !== 'en') {
      this.translate.getTranslation(this.voiceLang).subscribe();
    }
  }

  /**
   * Returns voice-control language from navigator.language.
   * - "en" if it starts with "en" or is not "es"/"fr"
   * - "es" if it starts with "es"
   * - "fr" if it starts with "fr"
   */
  getVoiceControlLanguage(): VoiceControlLang {
    if (typeof navigator === 'undefined' || !navigator.language) {
      return 'en';
    }
    const lang = navigator.language.toLowerCase();
    if (lang.startsWith('es')) return 'es';
    if (lang.startsWith('fr')) return 'fr';
    return 'en';
  }

  /**
   * Returns the translation for the given i18n key in the voice-control language.
   * Used for aria-labels so Voice Control speaks in the device language.
   * Falls back to English, then to the raw key if the voice lang is not loaded.
   */
  getVoiceLabel(key: string): string {
    if (!key) return '';
    const lang = this.voiceLang;
    const value = this.getTranslationForKey(lang, key);
    if (value != null && value !== key) return value;
    if (lang !== 'en') {
      const enValue = this.getTranslationForKey('en', key);
      if (enValue != null && enValue !== key) return enValue;
    }
    return key;
  }

  private getTranslationForKey(lang: string, key: string): string | null {
    const store = (this.translate as any).translations;
    if (!store) return null;
    const translations = store[lang];
    if (!translations || typeof translations !== 'object') {
      return null;
    }
    const value = key.split('.').reduce((obj: any, k) => obj?.[k], translations);
    return typeof value === 'string' ? value : null;
  }
}
